import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Eyebrow, MaskLines, Reveal } from "./motion";

export const Film = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const inset = useTransform(scrollYProgress, [0, 0.45], ["8%", "0%"]);
  const clip = useTransform(inset, (v) => `inset(${v} ${v} ${v} ${v} round 32px)`);
  const video = useRef(null);
  const inView = useInView(video, { amount: 0.3 });
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(true);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (inView && !started) v.play().catch(() => {});
    if (!inView && !v.paused) v.pause();
  }, [inView, started]);

  const playWithSound = () => {
    const v = video.current;
    v.currentTime = 0;
    v.muted = false;
    setMuted(false);
    setStarted(true);
    v.play().catch(() => {});
  };
  const togglePlay = () => { const v = video.current; if (v.paused) v.play().catch(() => {}); else v.pause(); };
  const toggleMute = () => { const v = video.current; v.muted = !v.muted; setMuted(v.muted); };

  return (
    <section ref={ref} className="relative bg-[#0C3440] text-white py-24 md:py-36 overflow-hidden" data-testid="film-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid gap-8 md:grid-cols-12 md:items-end mb-12">
          <div className="md:col-span-7">
            <Eyebrow light>{t.film.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-display text-[clamp(2.6rem,6vw,5.6rem)] leading-[0.95]">
              <MaskLines key={t.film.title} lines={[t.film.title]} lineClass={() => "italic"} />
            </h2>
          </div>
          <Reveal className="md:col-span-5"><p className="text-[15px] leading-relaxed text-[#B9D3D6]">{t.film.body}</p></Reveal>
        </div>
        <motion.div style={{ clipPath: clip }} className="relative aspect-video w-full overflow-hidden" data-testid="film-frame">
          <motion.div style={{ y, scale: 1.12 }} className="absolute inset-0">
            <video
              ref={video}
              src="/video/port-film.mp4"
              poster="/img/film-poster.jpg"
              muted
              loop={!started}
              playsInline
              preload="metadata"
              onPlay={() => setPaused(false)}
              onPause={() => setPaused(true)}
              onEnded={() => setStarted(false)}
              className="h-full w-full object-cover"
              data-testid="film-video"
            />
          </motion.div>
          <div className={`absolute inset-0 bg-gradient-to-t from-[#0A3440]/70 via-[#0A3440]/10 to-transparent transition-opacity duration-1000 ${started ? "opacity-40" : "opacity-100"}`} />
          {!started && (
            <button onClick={playWithSound} className="group absolute inset-0 grid place-items-center" data-testid="film-play-btn" aria-label={t.film.play}>
              <span className="flex flex-col items-center gap-3 sm:gap-5">
                <span className="relative grid h-16 w-16 sm:h-24 sm:w-24 md:h-28 md:w-28 place-items-center rounded-full border border-white/70 text-white bg-white/10 backdrop-blur-md transition-transform duration-700 group-hover:scale-110">
                  <span className="absolute inset-0 rounded-full border border-[#7FD9D6]/50 animate-ping-slow" />
                  <Play className="h-7 w-7 translate-x-0.5" />
                </span>
                <span className="hidden sm:block text-[12px] uppercase tracking-[0.3em] text-white">{t.film.play}</span>
              </span>
            </button>
          )}
          <div className="absolute bottom-3 end-3 sm:bottom-4 sm:end-4 flex gap-2" dir="ltr">
            <button onClick={togglePlay} className="grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/30 transition-colors" aria-label={paused ? "Play" : "Pause"} data-testid="film-toggle-play">
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            <button onClick={toggleMute} className="grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/30 transition-colors" aria-label={muted ? "Unmute" : "Mute"} data-testid="film-toggle-mute">
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
          <span className="absolute top-4 start-4 sm:top-auto sm:bottom-6 sm:start-6 text-[11px] sm:text-[12px] uppercase tracking-[0.24em] text-[#E3F1F2]" data-testid="film-status">{t.film.status}</span>
        </motion.div>
      </div>
    </section>
  );
};
